'use client';
import base from 'axios';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { apiConfig } from '@/config/apiConfig';
import { AuthContext, getAuthData } from './Authentication';
import { SessionContext } from './Session';
import { useRBACOptional } from '@/contexts/RBACContext';
import {
  fetchSubTenantMemberUserIds,
  resolveOrgIdForSubTenantMemberFetch,
} from '@/lib/subTenantMemberUserIds';
import { listAllAccounts } from '@/services/rbacApi';
import { getActiveTenantHeaders, resolveActiveRequestUserId } from '@/lib/activeTenantScope';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';

export const QRCodeAxiosContext = createContext<ReturnType<typeof base.create> | null>(null);

/** SWR key segment: sub-tenant scope / member IDs / account reset. */
export const QRCodeSubTenantMemberKeyContext = createContext<string>('');

/**
 * True once the org-owner's full member IDs have been fetched (or confirmed unnecessary).
 * Gates single-item campaign fetches so a member-created campaign is resolved before the request fires.
 */
export const QRCodeOwnerMemberIdsReadyContext = createContext<boolean>(true);

/** True while sub-tenant member IDs are not ready (SWR / fetches should wait). */
export function isQRCodeSubTenantMembersPending(scopeSignal: string): boolean {
  return scopeSignal.startsWith('loading:');
}

export const isCancel = base.isCancel;

/** FastAPI/Pydantic often returns `detail` as string | {msg}[] — Toast requires a string. */
function formatQRApiErrorDetail(detail: unknown): string | null {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((item: unknown) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && 'msg' in item) {
        const d = item as { msg?: unknown; loc?: unknown[] };
        const msg = d.msg != null ? String(d.msg) : '';
        const loc = Array.isArray(d.loc)
          ? d.loc.filter((x) => typeof x === 'string' && x !== 'body').join('.')
          : '';
        return loc ? `${loc}: ${msg}` : msg || JSON.stringify(item);
      }
      try {
        return JSON.stringify(item);
      } catch {
        return String(item);
      }
    });
    return parts.join('; ');
  }
  if (typeof detail === 'object' && detail !== null && 'msg' in detail) {
    return String((detail as { msg: unknown }).msg);
  }
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
}

function normalizeRequestPath(url: string | undefined): string {
  if (!url) return '';
  const noQuery = url.split('?')[0];
  if (noQuery.includes('://')) {
    try {
      return new URL(noQuery).pathname.replace(/^\/+|\/+$/g, '') || '';
    } catch {
      return noQuery.replace(/^\/+|\/+$/g, '');
    }
  }
  return noQuery.replace(/^\/+|\/+$/g, '');
}

/**
 * Paths that must receive member_user_ids when org owner scopes to a sub-tenant.
 * Match path suffixes so API Gateway stages (e.g. /prod/campaigns) still match — not only "campaigns".
 */
function qrCodePathNeedsMemberScope(pathNorm: string, method: string): boolean {
  const m = method.toLowerCase();
  const p = pathNorm.replace(/^\/+|\/+$/g, '');

  if (m === 'get' && /(^|\/)campaigns\/?$/.test(p)) return true;
  if (m === 'get' && /(^|\/)campaigns\/(summary|chart)\/?$/.test(p)) return true;
  if (m === 'get' && /(^|\/)campaigns\/[^/]+\/analytics\/?$/.test(p)) return true;
  if (m === 'post' && /(^|\/)campaigns\/[^/]+\/generate\/?$/.test(p)) return true;
  if (m === 'post' && /(^|\/)campaigns\/action\/bulk\/?$/.test(p)) return true;
  if (m === 'get' && /(^|\/)jobs\/[^/]+\/status\/?$/.test(p)) return true;

  const oneCampaign = p.match(/(^|\/)campaigns\/([^/]+)\/?$/);
  if (oneCampaign) {
    const seg = oneCampaign[2];
    if (seg === 'summary' || seg === 'chart' || seg === 'action') return false;
    if (m === 'get' || m === 'put' || m === 'patch') return true;
  }

  return false;
}

function getQRCodeInstance(
  auth: { token?: string | null; isAuthenticated?: boolean; userId?: string | number | null },
  user: { id?: unknown; userID?: unknown } | null,
  setSessionExpiredOpen: (open: boolean) => void,
  aggregateMemberIdsRef: MutableRefObject<number[] | null>,
  subTenantScopeActiveRef: MutableRefObject<boolean>,
  listingSubAccountIdRef: MutableRefObject<number | null>,
  ownerDefaultMemberIdsRef: MutableRefObject<number[] | null>,
  isOrgOwnerRef: MutableRefObject<boolean>
) {
  const baseURL = apiConfig.baseUrls.qrCode;
  const normalizedBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;

  if (!normalizedBaseURL || normalizedBaseURL.includes('your-lambda-url')) {
    console.warn('QR Code API URL is not properly configured. QR Code API calls will fail.');
    return base.create({
      baseURL: '',
      withCredentials: true,
    });
  }

  const axios = base.create({
    baseURL: normalizedBaseURL,
    withCredentials: true,
  });

  const auth_exclude: Array<string> = ['/health', '/redirect'];

  axios.interceptors.request.use((req) => {
    const isPublicEndpoint = auth_exclude.some((exclude) => req.url?.includes(exclude));
    const sessionUserId = user?.id || user?.userID;
    const activeUserId = resolveActiveRequestUserId(
      auth.userId != null ? String(auth.userId) : sessionUserId != null ? String(sessionUserId) : null
    );

    const currentAuth =
      typeof getAuthData === 'function'
        ? getAuthData()
        : { token: auth.token, isAuthenticated: auth.isAuthenticated };

    if (!isPublicEndpoint && currentAuth.isAuthenticated) {
      if (currentAuth.token) {
        req.headers['Authorization'] = `Bearer ${currentAuth.token}`;
      }
      Object.assign(req.headers, getActiveTenantHeaders());
      // getActiveTenantHeaders() skips X-Active-Account-Id when impersonating.
      // Set it from the effective listing scope so sub-account filter still works.
      if (listingSubAccountIdRef.current && !req.headers['X-Active-Account-Id']) {
        req.headers['X-Active-Account-Id'] = String(listingSubAccountIdRef.current);
      }
      if (activeUserId) {
        req.headers['x-user-id'] = String(activeUserId);
      }
    }

    if (!(req.data instanceof FormData)) {
      req.headers['Content-Type'] = req.headers['Content-Type'] || 'application/json';
    }
    req.headers['X-Frontend-Page'] = typeof window !== 'undefined' ? window.location.href : '';
    if (typeof document !== 'undefined' && document.cookie) {
      req.headers['cookies'] = document.cookie;
    }

    const method = (req.method || 'get').toLowerCase();
    const pathNorm = normalizeRequestPath(req.url);
    // Single-item GET (campaigns/{id} and its sub-resources like /analytics, /download-urls):
    // org owner in default context needs member IDs so campaigns created by other org
    // members are still found (matches the campaign owner's own access grant).
    const isSingleCampaignGet = (() => {
      if (method !== 'get') return false;
      const p = pathNorm.replace(/^\/+|\/+$/g, '');
      const m = p.match(/(^|\/)campaigns\/([^/]+)(?:\/(analytics|download-urls))?\/?$/);
      if (!m) return false;
      const seg = m[2];
      return seg !== 'summary' && seg !== 'chart' && seg !== 'action';
    })();
    if (subTenantScopeActiveRef.current && qrCodePathNeedsMemberScope(pathNorm, method)) {
      req.params = req.params || {};
      const ids = aggregateMemberIdsRef.current ?? [];
      (req.params as Record<string, string>).member_user_ids = ids.map(String).join(',');
    } else if (!subTenantScopeActiveRef.current && isOrgOwnerRef.current && isSingleCampaignGet) {
      const fallback = ownerDefaultMemberIdsRef.current;
      if (fallback != null) {
        req.params = req.params || {};
        (req.params as Record<string, string>).member_user_ids = fallback.map(String).join(',');
      }
    } else if (
      typeof window !== 'undefined' &&
      window.localStorage.getItem('impersonation') === 'true' &&
      activeUserId &&
      qrCodePathNeedsMemberScope(pathNorm, method)
    ) {
      req.params = req.params || {};
      (req.params as Record<string, string>).member_user_ids = String(activeUserId);
    } else if (req.params && typeof req.params === 'object' && 'member_user_ids' in req.params) {
      delete (req.params as Record<string, unknown>).member_user_ids;
    }

    return req;
  });

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (typeof window !== 'undefined' && window.navigator.onLine && err.response) {
        if (err.response.status === 401) {
          if (auth.isAuthenticated) {
            location.href = process.env.NEXT_PUBLIC_SELF_URL + '/v2/login';
          } else {
            setSessionExpiredOpen(true);
          }
        } else {
          const data = err.response?.data as { detail?: unknown; message?: unknown } | undefined;
          const fromDetail = data?.detail != null ? formatQRApiErrorDetail(data.detail) : null;
          const rawMessage = data?.message;
          const fromMessage =
            typeof rawMessage === 'string'
              ? rawMessage
              : rawMessage != null
                ? formatQRApiErrorDetail(rawMessage)
                : null;
          const status = err.response.status;
          const apiMessage = fromDetail || fromMessage;
          const isProd = process.env.NODE_ENV === 'production';
          err.errorCode = status;
          err.formattedError = (isProd && status >= 500 ? null : apiMessage) || `Request failed with status ${status}`;
        }
      } else {
        err.formattedError = 'An error occurred while connecting to the network';
      }
      return Promise.reject(err);
    }
  );
  return axios;
}

export default function QRCodeAxios({ children }: { children: React.ReactNode }) {
  const authData = useContext(AuthContext);
  const auth = authData || {};
  const sessionContext = useContext(SessionContext);
  const user = sessionContext?.user || null;
  const rbac = useRBACOptional();

  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [subTenantMemberKey, setSubTenantMemberKey] = useState('');
  const aggregateMemberIdsRef = useRef<number[] | null>(null);
  const subTenantScopeActiveRef = useRef(false);
  const listingSubAccountIdRef = useRef<number | null>(null);

  const ownerDefaultMemberIdsRef = useRef<number[] | null>(null);
  const isOrgOwnerRef = useRef<boolean>(false);
  const ownerUserIdRef = useRef<number | null>(null);
  isOrgOwnerRef.current = Boolean(rbac?.userContext?.is_org_owner);
  ownerUserIdRef.current = rbac?.userContext?.owner_user_id ? Number(rbac.userContext.owner_user_id) : null;
  const [ownerMemberIdsReady, setOwnerMemberIdsReady] = useState(false);

  const listingSubAccountId = rbac?.effectiveListingSubAccountId ?? null;
  listingSubAccountIdRef.current = listingSubAccountId;
  const isSubTenantListingScope = Boolean(listingSubAccountId);
  if (!isSubTenantListingScope) {
    aggregateMemberIdsRef.current = null;
  }

  useEffect(() => {
    if (!listingSubAccountId) {
      aggregateMemberIdsRef.current = null;
      setSubTenantMemberKey('');
      return;
    }
    const subId = listingSubAccountId;
    const orgId = resolveOrgIdForSubTenantMemberFetch(rbac?.currentOrgId, user);
    aggregateMemberIdsRef.current = [];
    setSubTenantMemberKey(`loading:${subId}`);
    let cancelled = false;
    (async () => {
      try {
        const ids = await fetchSubTenantMemberUserIds(subId, {
          orgId,
          bypassCache: true,
        });
        // Non-owner org members have their own account_id as effectiveListingSubAccountId,
        // which isn't a real sub-tenant — fetchSubTenantMemberUserIds returns [].
        // Fall back to the member's own user ID so the backend can scope to their campaigns.
        const effectiveIds =
          ids.length > 0 || isOrgOwnerRef.current
            ? ids
            : authData?.userId
              ? [Number(authData.userId)]
              : [];
        if (!cancelled) {
          aggregateMemberIdsRef.current = effectiveIds;
          const sorted = [...effectiveIds].sort((a, b) => a - b);
          setSubTenantMemberKey(`${subId}:${sorted.join(',')}`);
        }
      } catch {
        if (!cancelled) {
          aggregateMemberIdsRef.current = [];
          setSubTenantMemberKey(`${subId}:`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listingSubAccountId, rbac?.currentOrgId, user, authData?.userId]);

  // Sweep all org accounts so the owner can resolve member-created QR campaigns by URL.
  useEffect(() => {
    if (rbac?.isLoading) return;
    const isOwner = Boolean(rbac?.userContext?.is_org_owner);
    if (!isOwner) {
      ownerDefaultMemberIdsRef.current = null;
      setOwnerMemberIdsReady(true);
      return;
    }
    const orgId = rbac?.currentOrgId ?? null;
    if (!orgId) {
      const ownerExtId = ownerUserIdRef.current;
      ownerDefaultMemberIdsRef.current = ownerExtId != null ? [ownerExtId] : [];
      setOwnerMemberIdsReady(true);
      return;
    }
    setOwnerMemberIdsReady(false);
    let cancelled = false;
    (async () => {
      try {
        const allIds: number[] = [];
        const ownerExtId = ownerUserIdRef.current;
        if (ownerExtId != null && Number.isFinite(ownerExtId)) allIds.push(ownerExtId);
        const accounts = await listAllAccounts(orgId);
        await Promise.all(
          accounts.map(async (account) => {
            try {
              const ids = await fetchSubTenantMemberUserIds(account.id, { orgId, bypassCache: false });
              for (const id of ids) {
                if (!allIds.includes(id)) allIds.push(id);
              }
            } catch { /* skip */ }
          })
        );
        if (!cancelled) {
          ownerDefaultMemberIdsRef.current = allIds;
          setOwnerMemberIdsReady(true);
        }
      } catch {
        if (!cancelled) {
          const ownerExtId = ownerUserIdRef.current;
          ownerDefaultMemberIdsRef.current = ownerExtId != null ? [ownerExtId] : [];
          setOwnerMemberIdsReady(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [rbac?.isLoading, rbac?.userContext?.is_org_owner, rbac?.currentOrgId]);

  /**
   * Only attach member_user_ids once the member list fetch finished (not while loading: or
   * uninitialized ''). Otherwise the interceptor ran with [] and the param was dropped → owner-wide listings.
   */
  const subTenantMemberListReady =
    isSubTenantListingScope &&
    Boolean(subTenantMemberKey) &&
    !subTenantMemberKey.startsWith('loading:');
  subTenantScopeActiveRef.current = subTenantMemberListReady;

  /** First paint with sub-tenant scope but empty state must look "pending" so SWR does not fire unscoped. */
  const effectiveMemberKeyForSignal =
    !isSubTenantListingScope
      ? ''
      : !subTenantMemberKey || subTenantMemberKey.startsWith('loading:')
        ? `loading:${listingSubAccountId}`
        : subTenantMemberKey;

  const client = useMemo(
    () =>
      getQRCodeInstance(
        auth,
        user,
        setSessionExpiredOpen,
        aggregateMemberIdsRef,
        subTenantScopeActiveRef,
        listingSubAccountIdRef,
        ownerDefaultMemberIdsRef,
        isOrgOwnerRef
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recreate when auth identity or session user changes
    [authData, user]
  );

  const contextValue = auth?.isAuthenticated ? client : null;

  const qrCodeSubTenantScopeSignal = [
    effectiveMemberKeyForSignal,
    String(listingSubAccountId ?? ''),
    String(rbac?.currentAccountId ?? ''),
    String(rbac?.defaultAccountId ?? ''),
  ].join('|');

  return (
    <>
      <QRCodeAxiosContext.Provider value={contextValue}>
        <QRCodeSubTenantMemberKeyContext.Provider value={qrCodeSubTenantScopeSignal}>
          <QRCodeOwnerMemberIdsReadyContext.Provider value={ownerMemberIdsReady}>
            {children}
          </QRCodeOwnerMemberIdsReadyContext.Provider>
        </QRCodeSubTenantMemberKeyContext.Provider>
      </QRCodeAxiosContext.Provider>

      <Dialog open={sessionExpiredOpen} onClose={() => setSessionExpiredOpen(false)}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6">Session Expired</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>Your session has expired. Please log in again to continue.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionExpiredOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setSessionExpiredOpen(false);
              location.href = process.env.NEXT_PUBLIC_SELF_URL + '/v2/login';
            }}
          >
            Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
